import type { Handler } from './handler';
import type { ApiFetchGameFiltersPort } from './ports/api-fetch-game-filters.port';
import { tryCatch } from '~/utils/apostatudo/try-catch';
import { resolveAdapter } from '~/adapters/registry';
import gameFiltersDefaultFallback from '~/assets/json/games-filter-default.fallback.json';

const CACHE_TTL_MS = 30 * 1000;
const PAY_TODAY_CACHE_TTL_MS = 2 * 60 * 1000;
const MAX_CACHE_ENTRIES = 30;

type CacheEntry = { data: GameFiltersApiResponse; cachedAt: number };

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<GameFiltersApiResponse>>();

function getTtl(params: GameFiltersParams): number {
  return params.sort_by === 'pay_today' ? PAY_TODAY_CACHE_TTL_MS : CACHE_TTL_MS;
}

function getCacheKey(params: GameFiltersParams): string {
  return JSON.stringify({
    categories: [...(params.categories || [])].sort(),
    providers: [...(params.providers || [])].sort(),
    types: [...(params.types || [])].sort(),
    term: (params.term || '').trim(),
    sort: (params.sort || '').trim(),
    sort_by: (params.sort_by || '').trim(),
    sort_dir: (params.sort_dir || '').trim(),
    page: params.page || 1,
    per_page: params.per_page || 24,
  });
}

const DEFAULT_FALLBACK_KEY = getCacheKey({
  sort_by: 'pay_today',
  sort_dir: 'desc',
  page: 1,
  per_page: 30,
});

export const fetchGameFiltersHandler: Handler<Params, Ports, Result> = async (
  params: GameFiltersParams = {},
  ports = {
    apiFetchGameFilters: resolveAdapter('apiFetchGameFilters'),
  },
) => {
  const key = getCacheKey(params);
  const cached = cache.get(key);
  const ttl = getTtl(params);

  if (cached && Date.now() - cached.cachedAt < ttl) {
    return { filterData: cached.data };
  }

  const pending = inflight.get(key);

  if (pending) {
    const [data, err] = await tryCatch(pending);

    if (!err && data) return { filterData: data };
  }

  const promise = ports?.apiFetchGameFilters.handle(params);

  inflight.set(key, promise);

  const [filterData, fetchError] = await tryCatch(promise);

  inflight.delete(key);

  if (!fetchError && filterData) {
    if (cache.size >= MAX_CACHE_ENTRIES) {
      const oldestKey = cache.keys().next().value;

      if (oldestKey) cache.delete(oldestKey);
    }

    cache.set(key, { data: filterData, cachedAt: Date.now() });

    return { filterData };
  }

  console.error(
    '[fetchGameFiltersHandler] ❌ Erro ao buscar dados da API:',
    fetchError,
  );

  if (cached) return { filterData: cached.data };

  if (key === DEFAULT_FALLBACK_KEY) {
    return {
      filterData: gameFiltersDefaultFallback as GameFiltersApiResponse,
    };
  }

  return { filterData: null };
};

type Params = GameFiltersParams;

type Ports = { apiFetchGameFilters: ApiFetchGameFiltersPort };

type Result = Promise<{ filterData: GameFiltersApiResponse | null }>;
